import { HttpErrorResponse } from '@angular/common/http';

export type VaultAccountBlockedReason = 'inactive' | 'revoked' | 'unknown';

export interface VaultAccountBlockedState {
  message: string;
  reason: VaultAccountBlockedReason;
}

function extractHttpErrorDetail(err: HttpErrorResponse): string {
  const body = err.error as unknown;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (typeof body !== 'object' || body === null) return '';
  const record = body as Record<string, unknown>;
  const detail = record['detail'] ?? record['message'] ?? record['error'];
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  return '';
}

function classifyVaultAccountBlockedReason(detail: string): VaultAccountBlockedReason {
  const lower = detail.toLowerCase();
  // La eliminación (baja lógica) tiene prioridad sobre "inactivo": el back ahora marca
  // al usuario eliminado como INACTIVE en Simappe, así que un usuario retirado puede
  // traer también "inactivo" en el detail. Debe verse como "retirado del sistema"
  // (revoked, no reactivable), no como "desactivado" (inactive, reactivable).
  const revokedHint =
    lower.includes('eliminad') ||
    lower.includes('retirad') ||
    lower.includes('removed') ||
    lower.includes('revoked') ||
    lower.includes('dado de baja') ||
    lower.includes('baja lógica') ||
    lower.includes('baja logica') ||
    lower.includes('sin acceso') ||
    lower.includes('no tiene acceso') ||
    lower.includes('homolog');
  if (revokedHint) return 'revoked';
  if (lower.includes('inactivo') || lower.includes('desactivad')) return 'inactive';
  return 'unknown';
}

/**
 * 403 de cuenta bloqueada en Vault (inactivo/eliminado). No confundir con 403 de permiso de módulo.
 * Back DEV inactivo: "Usuario inactivo. Contacte al administrador."
 * Back DEV eliminado: "Usuario de Simappe (id=X) sin homologación en Vault..."
 */
export function resolveVaultAccountBlockedState(err: unknown): VaultAccountBlockedState | null {
  if (!(err instanceof HttpErrorResponse) || err.status !== 403) return null;

  const requestUrl = `${err.url || ''}`;
  const detail = extractHttpErrorDetail(err);
  const lower = detail.toLowerCase();
  const blockedHint =
    lower.includes('inactivo') ||
    lower.includes('eliminad') ||
    lower.includes('homolog') ||
    lower.includes('contacte al administrador') ||
    lower.includes('sin acceso a vault') ||
    lower.includes('no tiene acceso') ||
    (lower.includes('cuenta') && lower.includes('administrador'));
  const authMeForbidden = isVaultAuthMeUrl(requestUrl);

  if (!blockedHint && !authMeForbidden) return null;

  const reason = blockedHint ? classifyVaultAccountBlockedReason(detail) : 'unknown';
  return {
    message: buildVaultAccountBlockedUserMessage(reason),
    reason,
  };
}

/** Texto para el usuario final (sin detalles técnicos de Simappe/Vault). */
export function buildVaultAccountBlockedUserMessage(reason: VaultAccountBlockedReason): string {
  switch (reason) {
    case 'inactive':
      return (
        'Su cuenta está desactivada. No puede usar Vault hasta que un administrador la reactive. ' +
        'Contacte al administrador de su empresa.'
      );
    case 'revoked':
      return (
        'Su cuenta ya no tiene acceso a Vault, ' +
        'su usuario fue retirado del sistema'
      );
    default:
      return 'Su cuenta no tiene acceso a Vault. Contacte al administrador del sistema.';
  }
}

export function isVaultAuthMeUrl(url: string): boolean {
  return (url || '').split('?')[0].includes('/api/auth/me');
}

export function isVaultLoginRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/login' || path.endsWith('/login');
}

export const VAULT_ACCOUNT_BLOCKED_SCREEN_TITLE = 'Acceso restringido';

export const VAULT_LOGIN_BLOCKED_MESSAGE_KEY = 'vault_login_blocked_message';

export const VAULT_ACCOUNT_BLOCKED_CIRCUIT_KEY = 'vault_account_blocked_circuit';

export function openVaultAccountBlockedCircuit(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VAULT_ACCOUNT_BLOCKED_CIRCUIT_KEY, '1');
  }
}

export function isVaultAccountBlockedCircuitOpen(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(VAULT_ACCOUNT_BLOCKED_CIRCUIT_KEY) === '1';
}

export function resetVaultAccountBlockedCircuit(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(VAULT_ACCOUNT_BLOCKED_CIRCUIT_KEY);
  }
}

export function persistVaultLoginBlockedMessage(state: VaultAccountBlockedState): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(
    VAULT_LOGIN_BLOCKED_MESSAGE_KEY,
    JSON.stringify({
      title: VAULT_ACCOUNT_BLOCKED_SCREEN_TITLE,
      subtitle: state.message,
    }),
  );
}

export function consumeVaultLoginBlockedMessage(): { title: string; subtitle: string } | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(VAULT_LOGIN_BLOCKED_MESSAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(VAULT_LOGIN_BLOCKED_MESSAGE_KEY);
  try {
    const parsed = JSON.parse(raw) as { title?: unknown; subtitle?: unknown };
    if (typeof parsed.subtitle === 'string' && parsed.subtitle.trim()) {
      return {
        title:
          typeof parsed.title === 'string' && parsed.title.trim()
            ? parsed.title.trim()
            : VAULT_ACCOUNT_BLOCKED_SCREEN_TITLE,
        subtitle: parsed.subtitle.trim(),
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Icono del estado vacío UI kit según motivo de bloqueo. */
export function buildVaultAccountBlockedScreenIcon(
  reason: VaultAccountBlockedReason,
): 'Shield' | 'CircleX' | 'CircleAlert' {
  if (reason === 'revoked') return 'CircleX';
  if (reason === 'inactive') return 'Shield';
  return 'CircleAlert';
}
